import React, { useEffect, useRef, useState } from 'react';
import Input from './../input/Input';
import { Option } from '../dropdown/Dropdown';
import { useDebounce } from '../../hooks/useDebounce';

export interface IAutocompleteProps {
  options: Option[];
  onChange: (value: string) => void;
  disabled?: boolean;
  value: string;
  fetchOptions?: (input: string) => Promise<void>;
  placeholder?: string;
}
export const Autocomplete: React.FC<IAutocompleteProps> = ({
  options,
  onChange,
  disabled = false,
  value = '',
  fetchOptions,
  placeholder,
}) => {
  const [inputValue, setInputValue] = React.useState<string>(value);
  const [suggestions, setSuggestions] = useState<Option[]>([]);
  const autocompleteRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [turnOnSuggestions, setTurnOnSuggestions] = useState(false);
  const debouncedInputValue = useDebounce(inputValue, 500);

  useEffect(() => {
    if (!fetchOptions || !turnOnSuggestions) return;

    if (debouncedInputValue.length < 2) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchOptions(debouncedInputValue).then(() => {
      setLoading(false);
    });
  }, [debouncedInputValue, turnOnSuggestions]);

  useEffect(() => {
    if (!turnOnSuggestions) return;

    if (options?.some((x) => x.value === inputValue)) {
      return;
    }
    if (loading) {
      setSuggestions([{ label: 'Loading...', value: null }]);
    } else if (options?.length > 0) {
      setSuggestions(options);
    } else if (turnOnSuggestions) {
      setSuggestions([{ label: 'No results found', value: null }]);
    }
  }, [inputValue, options, loading, turnOnSuggestions]);

  useEffect(() => {
    onChange(inputValue);
  }, [inputValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    if (options === undefined && fetchOptions) {
      setLoading(true);
      fetchOptions(value || '').then(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      setInputValue((oldValue) => `${oldValue}`);
    }
  }, [loading]);

  const handleSelect = (option: Option) => {
    setInputValue(option.label);
    setSuggestions([]);
  };

  const renderSuggestions = (suggestions) => {
    return (
      <div ref={autocompleteRef} className="react-autocomplete__menu">
        {suggestions?.map((suggestion, index) => (
          <div
            className={`react-autocomplete__item ${inputValue == suggestion.label ? 'selected' : ''}`}
            key={index}
            onClick={() => {
              if (suggestion.value) handleSelect(suggestion);
            }}>
            {suggestion.label}
          </div>
        ))}
      </div>
    );
  };
  const handleFocus = () => {
    setTurnOnSuggestions(true);
    if (options?.length > 0) {
      setSuggestions(options);
    }
  };

  return (
    <div className="react-autocomplete" onFocus={handleFocus}>
      <Input
        type={'text'}
        disabled={disabled}
        value={inputValue}
        onChange={(changeEvent: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = changeEvent.target;
          setTurnOnSuggestions(true);
          setInputValue(value);
        }}
        placeholder={placeholder ?? 'Type here'}
        className="react-autocomplete__input"
      />
      {suggestions?.length > 0 && renderSuggestions(suggestions)}
    </div>
  );
};
